import React, { useState, useEffect, useMemo } from 'react';
import { classifyEligibleServices, computeAmortizationBreakdown, computeCreditEligibleARR, getServicePathway } from './pricingCalculatorUtils';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ColumnLayout,
  Badge,
  StatusIndicator,
  Table,
  Link,
  ProgressBar,
  Alert,
  Tabs,
  Grid,
  Button,
  Modal,
  Spinner,
  Popover,
  Icon,
  Toggle
} from '@cloudscape-design/components';
import { s3Service } from '../services/s3Service';
import ModernizationCharts from './ModernizationCharts';
import Bedrock3pPolicyPanel, { Bedrock3pEffectiveAmounts } from './Bedrock3pPolicyPanel';
import { isESCCalculatorUrl, getESCCurrency } from '../utils/escUtils';
import { sanitizeCalculatorUrl } from '../utils/sanitizeCalculatorUrl';

export const getTotalServiceSavings = (s) =>
  (s.ec2_sp_annual_savings || 0) +
  (s.rds_ri_annual_savings || 0) +
  (s.fargate_sp_annual_savings || 0) +
  (s.elasticache_ri_annual_savings || 0) +
  (s.redshift_ri_annual_savings || 0) +
  (s.opensearch_ri_annual_savings || 0);

export const isOptimizable = (s) =>
  (s.ec2_sp_annual_savings || 0) > 0 ||
  (s.rds_ri_annual_savings || 0) > 0 ||
  (s.fargate_sp_annual_savings || 0) > 0 ||
  (s.elasticache_ri_annual_savings || 0) > 0 ||
  (s.redshift_ri_annual_savings || 0) > 0 ||
  (s.opensearch_ri_annual_savings || 0) > 0 ||
  s.prev_generation === 'true' ||
  s.ola_recommended === 'true' ||
  !!s.storage_optimization;

export const getServicePlanType = (s) =>
  s.ec2_sp_plan_type ||
  s.rds_ri_plan_type ||
  s.fargate_sp_plan_type ||
  s.elasticache_ri_plan_type ||
  s.redshift_ri_plan_type ||
  s.opensearch_ri_plan_type ||
  '-';

// Parse an exclusion_breakdown value into an object (or null).
//
// The backend persists this as a Python dict repr (single-quoted keys/strings,
// None/True/False). We try real JSON first so that any already-JSON payload —
// or one with apostrophes inside string values — parses losslessly. Only when
// that fails do we fall back to the lossy Python-repr normalization.
//
// KNOWN LIMITATION: the fallback's quote swap (' -> ") still corrupts string
// values that contain an apostrophe (e.g. a `reason` like "doesn't qualify"),
// so such rows degrade to null here. The durable fix is for the Lambda to
// persist real JSON (json.dumps) instead of a Python repr; this helper would
// then always succeed on the JSON.parse path above.
const parseExclusionBreakdown = (breakdownString) => {
  if (!breakdownString) return null;
  if (typeof breakdownString === 'object') return breakdownString;
  // Real JSON (or anything already-valid) parses cleanly and preserves
  // apostrophes inside string values.
  try {
    return JSON.parse(breakdownString);
  } catch (e) {
    // Not valid JSON — fall through to Python-repr normalization.
  }
  try {
    const jsonString = breakdownString
      .replace(/'/g, '"')
      .replace(/None/g, 'null')
      .replace(/True/g, 'true')
      .replace(/False/g, 'false');
    return JSON.parse(jsonString);
  } catch (e) {
    console.error('Error parsing exclusion_breakdown:', e);
    return null;
  }
};

// Feature flag: 3-tier MAP eligibility badges (CREDIT ELIGIBLE / ARR ONLY / EXCLUDED).
const ENABLE_THREE_TIER_ELIGIBILITY = true;

// validatedCalculatorUrl: the calculator MAP validated (pricing_calculator.calculator_url),
// which the blind page passes. With several APFP links MAP need not validate the
// first, so the tab then opens that URL and reads its currency from it, never from
// another link. The full page and the Analyzer leave it unset.
// withholdVerdict: the blind page sets it, so the tab shows only raw pricing evidence:
// services, costs, ARR, percentages, exclusions and reconciliation inputs. It hides
// every evaluator output, even when the body still carries it (batpur, 2026-11-02):
// the program tier, qualification, threshold flags, per-service eligibility, funding
// amounts, the exclusion reasons ('not MAP eligible'), and the whole 3P Bedrock policy
// record (measurements, cap, the agent's effectiveAmounts and trust). The server's
// blind body has no partnerCash or awsCredits, so nothing here may read them.
const PricingCalculatorTab = ({ request, preloadedData, isESC = false, validatedCalculatorUrl, withholdVerdict = false }) => {
  const [calculatorDetails, setCalculatorDetails] = useState(preloadedData || null);
  const [loading, setLoading] = useState(!preloadedData);
  const [error, setError] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState('general');
  const [activeSubTab, setActiveSubTab] = useState('annual-run-rate');
  const [sapOracleEnabled, setSapOracleEnabled] = useState(false);
  const [dbAnalyticsEnabled, setDbAnalyticsEnabled] = useState(false);
  const [greenfieldEnabled, setGreenfieldEnabled] = useState(() => {
    // Default greenfield toggle based on APFP incentives.greenfield.eligible
    const incentives = request.validation_data?.complete_api_data?.fundRequest?.incentives || {};
    return incentives.greenfield?.eligible === true;
  });

  const validationData = request.validation_data?.detailed_validations || {};
  const pricingData = validationData.pricing_calculator || {};
  const fundRequestId = request.apfp_fundrequestid || request.id;

  // MAP 3P Bedrock policy (P530558159). The standalone Analyzer reads the live
  // block from its own response. The FR page reads the block and agent audit
  // persisted on the pricing_calculator result, not the calculator_details
  // recompute, so it shows what the decision was made against.
  const persistedBedrock3p = pricingData.bedrock_3p_policy || {};
  const bedrock3pBlock = withholdVerdict ? null : preloadedData
    ? calculatorDetails?.fundingEligibility?.bedrock3pPolicy
    : persistedBedrock3p.block;
  const bedrock3pAgent = preloadedData || withholdVerdict ? null : persistedBedrock3p.agent;
  const effectiveAmounts = Array.isArray(bedrock3pAgent?.effectiveAmounts?.rows) && bedrock3pAgent.effectiveAmounts.totals
    ? bedrock3pAgent.effectiveAmounts
    : null;

  // Detect ESC from calculator URL in request data if not explicitly passed
  const calculatorLinks = validatedCalculatorUrl
    ? [validatedCalculatorUrl]
    : request.validation_data?.complete_api_data?.fundRequest?.artifacts?.pricingCalculatorLinks || [];
  const detectedESC = isESC || calculatorLinks.some(url => isESCCalculatorUrl(url)) || calculatorDetails?.is_esc === true;
  const currency = getESCCurrency(detectedESC);

  useEffect(() => {
    if (preloadedData) return; // skip fetch — data already provided by caller
    loadCalculatorDetails();
  }, [fundRequestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCalculatorDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const details = await s3Service.getCalculatorDetails(fundRequestId);
      setCalculatorDetails(details);
    } catch (error) {
      console.error('Error loading calculator details:', error);
      setError(error.message || 'Failed to load pricing calculator data');
      setCalculatorDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Memoize service eligibility lookup map for O(1) access per row
  const eligibilityMap = useMemo(() => {
    const arr = calculatorDetails?.fundingEligibility?.serviceEligibility;
    return arr ? new Map(arr.map(s => [s.serviceCode, s])) : null;
  }, [calculatorDetails?.fundingEligibility?.serviceEligibility]);

  const getEligibilityBadge = (isEligible, serviceCode, forcedTier) => {
    // Use the funding eligibility data for 3-tier badge system
    if (ENABLE_THREE_TIER_ELIGIBILITY) {
      // Honor explicit tier from split rows (synthetic service codes won't exist in backend map)
      const tier = forcedTier || (eligibilityMap && eligibilityMap.get(serviceCode))?.tier;
      if (tier === 'credit_eligible') {
          return (
            <Popover
              dismissButton={false}
              position="top"
              size="medium"
              triggerType="custom"
              content={
                <Box>This service is on the MAP Included Services list. Its ARR counts toward your Qualification ARR and it can be tagged to receive Migrate &amp; Modernize credits.</Box>
              }
            >
              <Badge color="blue">CREDIT ELIGIBLE</Badge>
            </Popover>
          );
      } else if (tier === 'excluded') {
          return (
            <Popover
              dismissButton={false}
              position="top"
              size="medium"
              triggerType="custom"
              content={
                <Box>This service is always excluded from MAP calculations per program rules (List #1: Always Excluded Services).</Box>
              }
            >
              <Badge color="red">EXCLUDED</Badge>
            </Popover>
          );
      } else if (tier === 'arr_only') {
          return (
            <Popover
              dismissButton={false}
              position="top"
              size="medium"
              triggerType="custom"
              content={
                <Box>This service contributes to your Qualification ARR (which determines program tier and Partner Cash amounts) but is not on the MAP Included Services list — it cannot be tagged for credits.</Box>
              }
            >
              <Badge color="grey">ARR ONLY</Badge>
            </Popover>
          );
      } else if (tier) {
          console.warn(`Unknown eligibility tier "${tier}" for service ${serviceCode}`);
          return <Badge color="grey">ARR ONLY</Badge>;
      }
    }
    // Fallback to legacy behavior if funding data not available
    return (
      <Badge color={isEligible ? 'blue' : 'red'}>
        {isEligible ? 'MAP ELIGIBLE' : 'NOT ELIGIBLE'}
      </Badge>
    );
  };

  /**
   * Split services with mixed eligibility into separate rows.
   * Any service with both a qualified portion (map_qualified_mrr > 0) and an
   * excluded portion (monthly_always_excluded > 0) gets two rows so each maps
   * cleanly to a single eligibility status. Applies to all services universally
   * (CloudFront DTO, EC2 data transfer out, etc.).
   * Only active when ENABLE_THREE_TIER_ELIGIBILITY is true.
   */
  const splitMixedEligibilityServices = (services) => {
    if (!ENABLE_THREE_TIER_ELIGIBILITY || !services) return services || [];

    const result = [];
    for (const service of services) {
      const excludedMonthly = parseFloat(service.monthly_always_excluded || 0);
      const qualifiedMrr = parseFloat(service.map_qualified_mrr || 0);

      if (excludedMonthly > 0 && qualifiedMrr > 0) {
        // Resolve the real tier for the eligible portion from backend map
        const realTier = eligibilityMap?.get(service.service_code)?.tier || 'arr_only';
        // Row 1: eligible portion
        result.push({
          ...service,
          monthly_cost: qualifiedMrr,
          monthly_always_excluded: 0,
          map_qualified_mrr: qualifiedMrr,
          _forcedTier: realTier,
        });
        // Row 2: excluded portion
        let excludedSuffix = 'Excluded';
        const breakdown = service.exclusion_breakdown;
        if (breakdown) {
          const parsed = typeof breakdown === 'string'
            ? parseExclusionBreakdown(breakdown)
            : breakdown;
          if (parsed?.type === 'data_transfer') excludedSuffix = 'Data Transfer Out';
          else if (parsed?.type === 'cloudfront_data_transfer') excludedSuffix = 'CloudFront DTO';
          else if (parsed?.type === 'glacier_deep_archive') excludedSuffix = 'Glacier Deep Archive';
          else if (parsed?.type === 'quicksight_pro_spice') excludedSuffix = 'Pro/SPICE/Admin';
        }
        result.push({
          ...service,
          service_name: `${service.service_name} — ${excludedSuffix}`,
          service_code: `${service.service_code}__excluded`,
          monthly_cost: excludedMonthly,
          monthly_always_excluded: excludedMonthly,
          map_qualified_mrr: 0,
          _forcedTier: 'excluded',
        });
      } else {
        result.push(service);
      }
    }
    return result;
  };

  /**
   * Aggregate services by service_code (case-insensitive grouping)
   * Groups duplicate service line items and sums their costs
   * @param {Array} services - Array of service objects
   * @returns {Array} Aggregated services with summed costs and counts
   */
  const aggregateServicesByCode = (services) => {
    if (!services || services.length === 0) {
      return [];
    }

    // Group by service_code (case-insensitive)
    const grouped = services.reduce((acc, service) => {
      const serviceCode = service.service_code || '';
      const key = serviceCode.toLowerCase(); // Case-insensitive grouping
      
      if (!acc[key]) {
        acc[key] = {
          service_code: serviceCode, // Keep original casing from first occurrence
          service_name: service.service_name || '',
          monthly_cost: 0,
          map_qualified_mrr: 0,
          count: 0,
          modernization_pathway: service.modernization_pathway || 'Unknown',  // Use provided pathway
          region: service.region, // Keep first region
          _forcedTier: service._forcedTier || undefined, // Preserve tier from split rows
        };
      }
      
      // Sum costs and increment count
      acc[key].monthly_cost += service.monthly_cost || 0;
      acc[key].map_qualified_mrr += service.map_qualified_mrr || 0;
      acc[key].count += 1;
      
      return acc;
    }, {});
    
    // Convert to array and calculate derived fields
    const aggregatedArray = Object.values(grouped);
    const totalMonthlyCost = aggregatedArray.reduce((sum, s) => sum + s.monthly_cost, 0);
    
    return aggregatedArray.map(service => ({
      ...service,
      annual_cost: service.monthly_cost * 12,
      percentage: totalMonthlyCost > 0 
        ? (service.monthly_cost / totalMonthlyCost) * 100 
        : 0,
      map_eligible: service.map_qualified_mrr > 0
    }));
  };

  // Calculate summary data
  const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
  const services = calculatorDetails?.services || [];
  
  const totalARR = pricingData.annual_recurring_revenue || 
                   parseFloat((fundRequest.arrUsdAmount || '0').replace(/[^0-9.-]+/g, '')) ||
                   calculatorDetails?.modernizationPathways?.totalARR || 0;
  
  // Calculate Annual ARR (total before exclusions) and Total Excluded
  const annualARR = services.reduce((sum, service) => sum + ((service.monthly_cost * 12) + (service.upfront_cost || 0)), 0);
  const totalExcludedARR = services.reduce((sum, service) => sum + (service.monthly_always_excluded * 12 || 0), 0);
  
  // 3-year upfront amortization. The breakdown (total upfront, per-year portion
  // counted, and the deducted excess) is surfaced in the ARR Summary for display
  // transparency (P508436298). A 1-year-only estimate yields deduction === 0, so
  // the amortization card below is hidden — same behavior as before.
  const {
    totalUpfront: totalMultiYearUpfront,
    perYearPortion: perYearAmortizedPortion,
    deduction: amortizationDeduction,
  } = computeAmortizationBreakdown(services);

  // Calculate qualified ARR as Annual ARR - Total Excluded - Amortization
  const qualifiedARR = annualARR - totalExcludedARR - amortizationDeduction;

  // SP/RI optimization savings deducted from qualified ARR. Sums the same six
  // per-service savings fields the backend funding engine deducts (EC2 SP, RDS
  // RI, Fargate SP, ElastiCache RI, Redshift RI, OpenSearch RI), so this card's
  // "Final MAP Qualified ARR" equals the backend fundingData.qualificationARR.
  // (Previously skipped Fargate when fargateSPCovered, which diverged from the
  // backend's unconditional deduction and showed two different values under the
  // same label.)
  const totalOptimizationSavings = services.reduce((sum, s) => sum + getTotalServiceSavings(s), 0);
  
  const optimizationPercentage = pricingData.not_optimized_percentage || 0;
  const optimizationThresholdMet = pricingData.optimization_threshold_met;

  // Enhanced 4-column dashboard summary
  const renderEnhancedSummary = () => {
    const totalServices = services.length;

    // Use backend modernization data
    const modernizationData = calculatorDetails?.modernizationPathways || {};
    const modernServicesCount = modernizationData.pathways
      ?.filter(p => p.name !== 'Non Modern')
      .reduce((sum, p) => sum + p.serviceCount, 0) || 0;
    const modernServicesARR = modernizationData.modernARR || 0;
    const totalAnnualCost = modernizationData.totalARR || 0;
    
    // Calculate Annual ARR before exclusions (for Qualification ARR display only)
    const annualARRBeforeExclusions = services.reduce((sum, service) => sum + ((service.monthly_cost * 12) + (service.upfront_cost || 0)), 0);
    
    const eligibleServices = services.filter(service => service.map_qualified_mrr > 0).length;
    const eligibleServicesARR = computeCreditEligibleARR(calculatorDetails, services, eligibilityMap);
    const totalMonthlyCost = services.reduce((sum, service) => sum + (service.monthly_cost || 0), 0);
    
    // Calculate modern services percentage based on ARR, not count
    const modernServicesPercentage = totalAnnualCost > 0 ? ((modernServicesARR / totalAnnualCost) * 100).toFixed(1) : 0;
    // Calculate eligible services percentage based on ARR, not count
    const eligibleServicesPercentage = totalAnnualCost > 0 ? ((eligibleServicesARR / totalAnnualCost) * 100).toFixed(1) : 0;

    // Calculate excluded services data
    const excludedServices = services.filter(s => s.monthly_always_excluded > 0);
    const totalExcluded = excludedServices.reduce((sum, s) => {
      const breakdown = parseExclusionBreakdown(s.exclusion_breakdown);
      // Use arr from breakdown if available (handles upfront costs correctly)
      const annualAmount = breakdown?.arr !== undefined 
        ? breakdown.arr 
        : s.monthly_always_excluded * 12;
      return sum + annualAmount;
    }, 0);
    const excludedPercentage = annualARRBeforeExclusions > 0 
      ? (totalExcluded / annualARRBeforeExclusions) * 100 
      : 0;
    
    // Group by exclusion type
    const excludedByType = {};
    excludedServices.forEach(service => {
      const breakdown = parseExclusionBreakdown(service.exclusion_breakdown);
      if (!breakdown) return;
      
      const type = breakdown.type || 'other';
      if (!excludedByType[type]) {
        excludedByType[type] = {
          totalAnnual: 0,
          services: []
        };
      }
      
      // Use the arr field from breakdown if available (handles upfront costs correctly)
      // Otherwise fall back to monthly_always_excluded * 12
      const annualAmount = breakdown.arr !== undefined 
        ? breakdown.arr 
        : service.monthly_always_excluded * 12;
      
      excludedByType[type].totalAnnual += annualAmount;
      excludedByType[type].services.push({
        name: service.service_name,
        breakdown: breakdown
      });
    });

    return (
      <Container header={<Header variant="h2">Dashboard Overview</Header>}>
        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
          {/* MAP ARR Summary (renamed from "MAP ARR Qualification" per P472162118 for clarity) */}
          <Container>
            <Box variant="h3" color="text-status-info">MAP ARR Summary</Box>
            <SpaceBetween size="s">
              {/* Calculator Provided ARR */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Calculator Provided ARR</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold">
                  {formatCurrency(annualARRBeforeExclusions)}
                </Box>
              </div>
              
              {/* MAP Excluded Services */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>MAP Excluded Services</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  ({formatCurrency(totalExcluded)})
                </Box>
                <Box variant="small" color="text-status-inactive">
                  Data Transfer, Support, Glacier
                </Box>
              </div>

              {/* 3-Year Amortization */}
              {amortizationDeduction > 0 && (
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>3-Year Amortization</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  ({formatCurrency(amortizationDeduction)})
                </Box>
                <Box variant="small" color="text-status-inactive">
                  Multi-year upfront ÷ 3
                </Box>
                {/* Amortization savings breakdown (P508436298): show how the
                    deducted excess was derived so the adjustment is transparent. */}
                <Box variant="small" color="text-status-inactive" margin={{ top: 'xxs' }}>
                  Total upfront {formatCurrency(totalMultiYearUpfront)} ÷ 3 = {formatCurrency(perYearAmortizedPortion)}/yr counted; excess {formatCurrency(amortizationDeduction)} deducted
                </Box>
              </div>
              )}
              
              {/* Non-Optimized ARR */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Non-Optimized ARR</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  ({formatCurrency(totalOptimizationSavings)})
                </Box>
                <Box variant="small" color="text-status-inactive">
                  RI/SP Optimization Opportunity
                </Box>
              </div>
              
              {/* Total Deductions */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Total Deductions</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  ({formatCurrency(totalExcluded + amortizationDeduction + totalOptimizationSavings)})
                </Box>
              </div>
              
              {/* Final MAP Qualified ARR */}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Final MAP Qualified ARR</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-success">
                  {formatCurrency(qualifiedARR - totalOptimizationSavings)}
                </Box>
              </div>
              
              {/* ARR Variance Message from Agent Validation */}
              {(() => {
                const approvalSummary = request.approval_summary || {};
                const keyFindings = approvalSummary.key_findings || [];

                // Match the new "Qualification ARR" label and the legacy
                // "Pricing Calculator ARR" wording (older stored findings).
                const arrVarianceMessage = keyFindings.find(finding => {
                  const f = finding.toLowerCase();
                  return f.includes('qualification arr') || f.includes('calculator arr');
                });

                if (arrVarianceMessage) {
                  const isSuccess = arrVarianceMessage.includes('✅') ||
                                   !arrVarianceMessage.toLowerCase().includes('exceed');

                  // P472130542: Assess-phase ARR variance is informational
                  // only (does not trigger rejection). Rendering it as
                  // Alert type="error" produced a red ❌ icon + red box
                  // that reviewers read as a critical finding. On Assess,
                  // downgrade the non-success case to "warning" (amber),
                  // matching the "ARR VARIANCE WARNING" banner rendered
                  // at the top of the tab from the backend warnings
                  // block. Mobilize keeps error styling because variance
                  // there actually triggers rejection per the MAP SOP.
                  const migrationPhase = (
                    request.validation_data?.complete_api_data?.fundRequest?.migrationPhase || ''
                  ).toLowerCase();
                  const isAssess = migrationPhase === 'assess';
                  const alertType = isSuccess
                    ? 'success'
                    : (isAssess ? 'warning' : 'error');
                  const iconAriaLabel = isSuccess
                    ? 'Success'
                    : (isAssess ? 'Warning' : 'Error');

                  return (
                    <Alert
                      type={alertType}
                      statusIconAriaLabel={iconAriaLabel}
                    >
                      {arrVarianceMessage}
                    </Alert>
                  );
                }
                return null;
              })()}
            </SpaceBetween>
          </Container>
          
          {/* MAP Always Excluded Services */}
          <Container>
            <Box variant="h3" color="text-status-info">MAP Always Excluded Services</Box>
            <SpaceBetween size="s">
              {/* Critical finding alert based on threshold (€15k for ESC, $15k for standard) */}
              {withholdVerdict ? null : totalExcluded > 15000 ? (
                <Alert type="error" statusIconAriaLabel="Error">
                  Critical finding: non-eligible services exceed {formatCurrency(15000)} ARR threshold — recommend rejection
                </Alert>
              ) : totalExcluded > 0 ? (
                <Alert type="info" statusIconAriaLabel="Info">
                  While approved, non-eligible services should not be included in qualified ARR for future reference
                </Alert>
              ) : null}
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline' }}>Total Excluded</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  {formatCurrency(totalExcluded)}
                </Box>
                <Box variant="small" color="text-status-inactive">
                  ({excludedPercentage.toFixed(1)}% of ARR)
                </Box>
              </div>
              
              {Object.keys(excludedByType).length > 0 && (
                <div>
                  <Box variant="awsui-key-label">
                    <span style={{ textDecoration: 'underline', fontStyle: 'italic' }}>Breakdown</span>
                  </Box>
                  <SpaceBetween size="xs">
                    {Object.entries(excludedByType).map(([type, data]) => (
                      <Box key={type}>
                        <Box variant="strong">
                          {type === 'aws_support' ? 'AWS Support' : 
                           type === 'data_transfer' ? 'AWS Data Transfer Out' : 
                           type === 'glacier_deep_archive' ? 'Amazon S3 Glacier Deep Archive' : 
                           type === 'cloudfront_data_transfer' ? 'CloudFront Data Transfer Out' :
                           type === 'quicksight_pro_spice' ? 'QuickSight Pro/SPICE/Admin' : type}
                        </Box>
                        <Box color="text-status-error">
                          {formatCurrency(data.totalAnnual)}
                        </Box>
                        {!withholdVerdict && data.services[0]?.breakdown?.reason && (
                          <Box variant="small" color="text-status-inactive">
                            {data.services[0].breakdown.reason}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </SpaceBetween>
                </div>
              )}
            </SpaceBetween>
          </Container>
          
          {/* Optimization Opportunities */}
          <Container>
            <Box variant="h3" color="text-status-info">Optimization Opportunities</Box>
            <SpaceBetween size="s">
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ textDecoration: 'underline', fontStyle: 'italic' }}>Potential ARR Savings</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  {(() => {
                    const services = calculatorDetails?.services || [];
                    const totalSavings = services.reduce((sum, s) => sum + getTotalServiceSavings(s), 0);
                    return formatCurrency(totalSavings);
                  })()}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ textDecoration: 'underline', fontStyle: 'italic' }}>Non-Optimized %</span>
                </Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-error">
                  {(() => {
                    if (optimizationPercentage != null) {
                      return `${Number(optimizationPercentage).toFixed(1)}%`;
                    }
                    const services = calculatorDetails?.services || [];
                    const totalSavings = services.reduce((sum, s) => sum + getTotalServiceSavings(s), 0);
                    const totalCalcARR = services.reduce((sum, s) => sum + ((s.monthly_cost * 12) + (s.upfront_cost || 0)), 0);
                    const percentage = totalCalcARR > 0 ? (totalSavings / totalCalcARR) * 100 : 0;
                    return `${percentage.toFixed(1)}%`;
                  })()}
                </Box>
                <Box variant="small" color="text-status-inactive">
                  of Total ARR
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">
                  <span style={{ textDecoration: 'underline', fontStyle: 'italic' }}>Optimizable Services</span>
                </Box>
                <Box color="text-status-info">
                  {(() => {
                    const services = calculatorDetails?.services || [];
                    const optimizable = services.filter(isOptimizable);
                    const total = optimizable.length;
                    const names = optimizable
                      .map(s => s.service_name || s.service_code)
                      .filter((v, i, arr) => arr.indexOf(v) === i);
                    return total > 0 ? `${total} optimizable (${names.join(', ')})` : 'No services optimizable';
                  })()}
                </Box>
              </div>
              {/* Download Optimization Report */}
              {(() => {
                const allServices = calculatorDetails?.services || [];
                const optimizableServices = allServices.filter(isOptimizable);

                if (optimizableServices.length === 0) {
                  return (
                    <Box color="text-status-inactive" fontSize="body-s">
                      No optimization opportunities found
                    </Box>
                  );
                }

                const downloadOptimizationReport = () => {
                  const parseConfigSummary = (config, field) => {
                    if (!config) return null;
                    const match = config.match(new RegExp(`${field}\\s*\\(([^)]+)\\)`));
                    return match ? match[1] : null;
                  };

                  const getServiceSavings = getTotalServiceSavings;

                  const getRecommendationType = (s) => {
                    const types = [];
                    if (s.ec2_sp_annual_savings > 0) types.push('EC2 SP');
                    if (s.rds_ri_annual_savings > 0) types.push('RDS RI');
                    if (s.fargate_sp_annual_savings > 0) types.push('Fargate SP');
                    if (s.elasticache_ri_annual_savings > 0) types.push('Elasticache RI');
                    if (s.redshift_ri_annual_savings > 0) types.push('Redshift RI');
                    if (s.opensearch_ri_annual_savings > 0) types.push('Opensearch RI');
                    if (s.prev_generation === 'true') types.push('Instance Upgrade');
                    if (s.storage_optimization) types.push('Storage Upgrade');
                    if (s.ola_recommended === 'true') types.push('OLA');
                    return types.join(', ') || '-';
                  };

                  const getPlanType = getServicePlanType;

                  const currencySymbol = detectedESC ? '€' : '$';
                  const headers = [
                    'Service Name', 'Service Code', 'Region',
                    'Current Instance Type', 'Recommended Instance Type',
                    `Monthly Cost (${currencySymbol})`, `Annual Savings (${currencySymbol})`,
                    'Recommended Pricing Strategy', 'Recommendation Type'
                  ];

                  const rows = optimizableServices.map(s => [
                    s.service_name?.trim() || '-',
                    s.service_code || '-',
                    s.region || '-',
                    parseConfigSummary(s.config_summary, 'Instance Type') || parseConfigSummary(s.config_summary, 'Advance EC2 instance') || '-',
                    s.recommended_instance_type || '-',
                    s.monthly_cost?.toFixed(2) || '0.00',
                    getServiceSavings(s).toFixed(2),
                    getPlanType(s),
                    getRecommendationType(s)
                  ]);

                  const totalSavings = optimizableServices.reduce((sum, s) => sum + getServiceSavings(s), 0);
                  rows.push(['TOTAL', '', '', '', '', '', totalSavings.toFixed(2), '', '']);

                  const csvContent = [headers, ...rows]
                    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                    .join('\n');

                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `optimization-report-${fundRequestId}.csv`;
                  link.click();
                  URL.revokeObjectURL(url);
                };

                return (
                  <Button iconName="download" variant="normal" onClick={downloadOptimizationReport}>
                    Download Optimization Report
                  </Button>
                );
              })()}
            </SpaceBetween>
          </Container>
        </Grid>
      </Container>
    );
  };

  const renderSummarySection = () => (
    <Container>
      <ColumnLayout columns={3} variant="text-grid">
        {/* General */}
        <div>
          <Box variant="h3">General</Box>
          <SpaceBetween size="s">
            <div>
              <Box variant="awsui-key-label">Annual Run Rate</Box>
              <Box fontSize="heading-l" fontWeight="bold">
                {formatCurrency(totalARR)}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Services estimate processed</Box>
              <Box color="text-status-success">
                ✓ {calculatorDetails?.serviceCount || 0}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Modernization Index</Box>
              <Box>{((qualifiedARR / totalARR) * 100 || 0).toFixed(1)}%</Box>
            </div>
          </SpaceBetween>
        </div>

        {/* Optimizations */}
        <div>
          <Box variant="h3">Optimizations</Box>
          <SpaceBetween size="s">
            <div>
              <Box variant="awsui-key-label">Recommendations</Box>
              <Box color={optimizationThresholdMet ? 'text-status-success' : 'text-status-error'}>
                {optimizationThresholdMet ? '✓ 0' : '⚠ 1'}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Recommendations may lower ARR by</Box>
              <Box>{formatCurrency(0)}</Box>
            </div>
          </SpaceBetween>
        </div>

        {/* Funding Insights */}
        <div>
          <Box variant="h3">Funding Insights</Box>
          <SpaceBetween size="s">
            <div>
              <Box variant="awsui-key-label">Qualification ARR</Box>
              <Box fontSize="heading-m" fontWeight="bold" color="text-status-success">
                {formatCurrency(qualifiedARR)}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Migration Acceleration Program</Box>
              <Box>{(() => {
                // Determine MAP program based on ARR amount
                // ESC uses EUR thresholds: MAP Lite €100K-€2M, MAP Large €2M+
                // Standard AWS uses USD thresholds
                const services = calculatorDetails?.services || [];
                const totalAnnualCost = services.reduce((sum, service) => sum + ((service.monthly_cost * 12) + (service.upfront_cost || 0)), 0);
                const largeThreshold = detectedESC ? 2000000 : 500000;
                return totalAnnualCost > largeThreshold ? 'MAP LARGE' : 'MAP LITE';
              })()}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Proof of Concept</Box>
              <Box>{formatCurrency(0)}</Box>
            </div>
          </SpaceBetween>
        </div>
      </ColumnLayout>
    </Container>
  );

  const renderAnnualRunRate = () => {
    // Use backend pathway data for pie chart
    const modernizationData = calculatorDetails?.modernizationPathways || {};
    const modernServicesARR = modernizationData.modernARR || 0;
    // Use the extracted helper — same function tested in pricingCalculatorUtils.test.
    // Withheld from a blind reviewer: it sums the per-service eligibility tiers.
    const eligibleServicesARR = withholdVerdict ? 0 : computeCreditEligibleARR(calculatorDetails, services, eligibilityMap);
    const pathwayGroups = {};
    let totalCost = 0;

    if (modernizationData.pathways) {
      modernizationData.pathways.forEach(pathway => {
        pathwayGroups[pathway.name] = pathway.arr;
        totalCost += pathway.arr;
      });
    }

    return (
      <SpaceBetween size="l">
        <Container>
          <ColumnLayout columns={2}>
            {/* Bar Chart Area */}
            <div>
              <Box variant="h3">Annual Run Rate</Box>
              <Box variant="small">{currency}</Box>
              
              {/* Simple bar representation */}
              <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'end', height: '200px', gap: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div 
                      style={{ 
                        width: '60px', 
                        height: `${Math.min((qualifiedARR / totalARR) * 180 || 0, 180)}px`, 
                        backgroundColor: '#1f77b4',
                        marginBottom: '5px'
                      }}
                    />
                    <Box variant="small" fontWeight="bold">{formatCurrency(qualifiedARR)}</Box>
                    <Box variant="small">Qualification ARR</Box>
                  </div>
                  {eligibleServicesARR > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div 
                      style={{ 
                        width: '60px', 
                        height: `${Math.min((eligibleServicesARR / totalARR) * 180 || 0, 180)}px`, 
                        backgroundColor: '#ff7f0e',
                        marginBottom: '5px'
                      }}
                    />
                    <Box variant="small" fontWeight="bold">{formatCurrency(eligibleServicesARR)}</Box>
                    <Box variant="small">MAP Eligible ARR</Box>
                  </div>
                  )}
                  {modernServicesARR > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div 
                      style={{ 
                        width: '60px', 
                        height: `${Math.min((modernServicesARR / totalARR) * 180 || 0, 180)}px`, 
                        backgroundColor: '#2ca02c',
                        marginBottom: '5px'
                      }}
                    />
                    <Box variant="small" fontWeight="bold">{formatCurrency(modernServicesARR)}</Box>
                    <Box variant="small">Modernization ARR</Box>
                  </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div 
                      style={{ 
                        width: '60px', 
                        height: '180px', 
                        backgroundColor: '#d62728',
                        marginBottom: '5px'
                      }}
                    />
                    <Box variant="small" fontWeight="bold">{formatCurrency(totalARR)}</Box>
                    <Box variant="small">Total Annual Run Rate</Box>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div style={{ marginTop: '20px' }}>
                <SpaceBetween size="xs">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#1f77b4' }} />
                    <Box variant="small">Qualification ARR</Box>
                  </div>
                  {eligibleServicesARR > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#ff7f0e' }} />
                    <Box variant="small">MAP Eligible ARR</Box>
                  </div>
                  )}
                  {modernServicesARR > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#2ca02c' }} />
                    <Box variant="small">Modernization ARR</Box>
                  </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#d62728' }} />
                    <Box variant="small">Total Annual Run Rate</Box>
                  </div>
                </SpaceBetween>
              </div>
            </div>

            {/* Pie Chart Area */}
            <div>
              <Box variant="h3">Modernization Index</Box>
              
              {/* Simple pie chart representation */}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <div 
                  style={{ 
                    width: '150px', 
                    height: '150px', 
                    borderRadius: '50%',
                    backgroundColor: Object.keys(pathwayGroups).length > 1 ? 
                      `conic-gradient(${Object.entries(pathwayGroups).map(([pathway, cost], index) => {
                        const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b'];
                        const percentage = (cost / totalCost) * 100;
                        return `${colors[index % colors.length]} ${index === 0 ? 0 : Object.entries(pathwayGroups).slice(0, index).reduce((sum, [, c]) => sum + (c / totalCost) * 100, 0)}% ${Object.entries(pathwayGroups).slice(0, index + 1).reduce((sum, [, c]) => sum + (c / totalCost) * 100, 0)}%`;
                      }).join(', ')})` : '#1f77b4'
                  }}
                />
              </div>

              <Box variant="small" textAlign="center" padding={{ top: 's' }}>
                {formatCurrency(totalCost)} • 100%
              </Box>
            </div>
          </ColumnLayout>
        </Container>

        {/* ARR Breakdown List */}
        <Container>
          <SpaceBetween size="s">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#1f77b4' }} />
              <Box variant="strong">Qualification ARR:</Box>
              <Box>Heavy State Annual Run Rate (optimized) used for funding</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#ff7f0e' }} />
              <Box variant="strong">MAP Eligible ARR:</Box>
              <Box>Annual Run Rate generated by the eligible services</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#2ca02c' }} />
              <Box variant="strong">Modernization ARR:</Box>
              <Box>Annual Run Rate generated by modern services</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#9467bd' }} />
              <Box variant="strong">SAP and Oracle Eligible ARR:</Box>
              <Box>Annual Run Rate generated by the SAP and Oracle eligible services</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#8c564b' }} />
              <Box variant="strong">DB and Analytics Eligible ARR:</Box>
              <Box>Annual Run Rate generated by the DB and Analytics eligible services</Box>
            </div>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    );
  };

  // Enhanced Services Table with Modal Details
  const [selectedService, setSelectedService] = useState(null);
  const [showServiceModal, setShowServiceModal] = useState(false);

  const renderServiceModal = () => (
    <Modal
      onDismiss={() => setShowServiceModal(false)}
      visible={showServiceModal}
      size="large"
      header={selectedService?.service_name || 'Service Details'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setShowServiceModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setShowServiceModal(false)}>
              Close
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {selectedService && (
        <SpaceBetween size="l">
          <Container header={<Header variant="h3">Service Information</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <SpaceBetween size="s">
                <div>
                  <Box variant="awsui-key-label">Service Code</Box>
                  <Box>{selectedService.service_code}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Service Name</Box>
                  <Box>{selectedService.service_name}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Region</Box>
                  <Box>{selectedService.region || 'us-east-1'}</Box>
                </div>
              </SpaceBetween>
              <SpaceBetween size="s">
                <div>
                  <Box variant="awsui-key-label">Monthly Cost</Box>
                  <Box fontSize="heading-m" fontWeight="bold">
                    {formatCurrency(selectedService.monthly_cost)}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Annual Cost</Box>
                  <Box>{formatCurrency(selectedService.monthly_cost * 12)}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">MAP Qualified MRR</Box>
                  <Box>{formatCurrency(selectedService.map_qualified_mrr)}</Box>
                </div>
              </SpaceBetween>
            </ColumnLayout>
          </Container>
          
          <Container header={<Header variant="h3">Modernization Analysis</Header>}>
            <SpaceBetween size="s">
              <div>
                <Box variant="awsui-key-label">Modernization Pathway</Box>
                <Badge color={selectedService.modernization_pathway !== 'Non Modern' ? 'blue' : 'red'}>
                  {selectedService.modernization_pathway}
                </Badge>
              </div>
              <div>
                <Box variant="awsui-key-label">MAP Eligibility</Box>
                {getEligibilityBadge(selectedService.map_qualified_mrr > 0, selectedService.service_code)}
              </div>
              <div>
                <Box variant="awsui-key-label">Optimization Potential</Box>
                <StatusIndicator type={selectedService.map_qualified_mrr > 0 ? 'success' : 'warning'}>
                  {selectedService.map_qualified_mrr > 0 ? 'Optimized' : 'Review Recommended'}
                </StatusIndicator>
              </div>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      )}
    </Modal>
  );

  const renderEnhancedServicesTable = () => {
    if (!calculatorDetails?.services) {
      return (
        <Container>
          <Box textAlign="center" color="inherit">
            <b>No service details available</b>
          </Box>
        </Container>
      );
    }

    // Enrich services with pathway from backend
    const modernizationData = calculatorDetails?.modernizationPathways || {};
    const servicesWithPathway = calculatorDetails.services.map(service => ({
      ...service,
      modernization_pathway: getServicePathway(service.service_code, modernizationData)
    }));

    const aggregatedServices = aggregateServicesByCode(splitMixedEligibilityServices(servicesWithPathway));

    return (
      <Container>
        <Table
          columnDefinitions={[
            {
              id: 'service_name',
              header: 'Service',
              cell: item => item.service_name,
              sortingField: 'service_name'
            },
            {
              id: 'service_code',
              header: 'Service Code',
              cell: item => item.service_code,
              sortingField: 'service_code'
            },
            {
              id: 'modernization_pathway',
              header: 'Modernization',
              cell: item => (
                <Badge color={item.modernization_pathway !== 'Non Modern' ? 'blue' : 'red'}>
                  {item.modernization_pathway}
                </Badge>
              )
            },
            ...(withholdVerdict ? [] : [{
              id: 'eligibility',
              header: 'MAP Eligibility',
              cell: item => getEligibilityBadge(item.map_eligible, item.service_code, item._forcedTier)
            }]),
            {
              id: 'monthly_cost',
              header: 'Monthly Cost',
              cell: item => formatCurrency(item.monthly_cost),
              sortingField: 'monthly_cost'
            },
            {
              id: 'annual_cost',
              header: 'Annual Cost',
              cell: item => formatCurrency(item.annual_cost),
              sortingField: 'annual_cost'
            },
            {
              id: 'count',
              header: 'Line Items',
              cell: item => item.count,
              sortingField: 'count'
            }
          ]}
          items={aggregatedServices}
          loadingText="Loading services"
          sortingDisabled={false}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No services found</b>
            </Box>
          }
          header={
            <Header
              counter={`(${aggregatedServices.length})`}
              description="Services aggregated by service code for easier reading"
            >
              Service Estimates
            </Header>
          }
          pagination
        />
        {renderServiceModal()}
      </Container>
    );
  };

  const renderEligibleServices = () => {
    const services = calculatorDetails?.services || [];
    
    // Filter to only services with map_qualified_mrr > 0
    const qualifiedServicesRaw = services.filter(service => 
      (service.map_qualified_mrr || 0) > 0
    );

    // Separate credit-eligible from ARR-only using the extracted utility function.
    const { creditEligible: creditEligibleRaw, arrOnly: arrOnlyRaw } =
      classifyEligibleServices(qualifiedServicesRaw, eligibilityMap);
    
    // Aggregate each group by service_code
    const creditEligibleServices = aggregateServicesByCode(creditEligibleRaw);
    const arrOnlyServices = aggregateServicesByCode(arrOnlyRaw);

    const serviceColumns = [
      {
        id: 'service_code',
        header: 'Service Code',
        cell: item => item.service_code,
        sortingField: 'service_code'
      },
      {
        id: 'service_name',
        header: 'Service Name',
        cell: item => item.service_name,
        sortingField: 'service_name'
      },
      {
        id: 'annual_cost',
        header: 'Annual Cost',
        cell: item => formatCurrency(item.annual_cost),
        sortingField: 'annual_cost'
      },
      {
        id: 'monthly_cost',
        header: 'Monthly Cost',
        cell: item => formatCurrency(item.monthly_cost),
        sortingField: 'monthly_cost'
      },
      {
        id: 'count',
        header: 'Line Items',
        cell: item => item.count,
        sortingField: 'count'
      },
      {
        id: 'map_qualified_mrr',
        header: 'MAP Qualified MRR',
        cell: item => formatCurrency(item.map_qualified_mrr),
        sortingField: 'map_qualified_mrr'
      }
    ];
    
    return (
      <SpaceBetween size="l">
        <Container>
          <Table
            columnDefinitions={serviceColumns}
            items={creditEligibleServices}
            loadingText="Loading eligible services"
            sortingDisabled={false}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No MAP credit-eligible services found</b>
                <Box variant="p" color="inherit">
                  Services must be on the MAP Included Services list and have MAP qualified MRR greater than zero to be credit-eligible.
                </Box>
              </Box>
            }
            header={
              <Header
                counter={`(${creditEligibleServices.length})`}
                description="Services that can be tagged for Migrate & Modernize credits"
              >
                MAP Credit-Eligible Services
              </Header>
            }
            pagination
          />
        </Container>
        {arrOnlyServices.length > 0 && (
          <Container>
            <Alert type="info" statusIconAriaLabel="Info">
              These services contribute to your Qualification ARR (which determines program tier and Partner Cash) but are not on the MAP Included Services list and cannot receive M&amp;M credits.
            </Alert>
            <Table
              columnDefinitions={serviceColumns}
              items={arrOnlyServices}
              loadingText="Loading ARR-only services"
              sortingDisabled={false}
              empty={
                <Box textAlign="center" color="inherit">
                  <b>No ARR-only services</b>
                </Box>
              }
              header={
                <Header
                  counter={`(${arrOnlyServices.length})`}
                  description="Services that count toward Qualification ARR but are not credit-eligible"
                >
                  Qualification ARR Only
                </Header>
              }
              pagination
            />
          </Container>
        )}
      </SpaceBetween>
    );
  };

  // Human-readable label for an exclusion_breakdown.type
  const exclusionTypeLabel = (type) => {
    switch (type) {
      case 'aws_support': return 'AWS Support';
      case 'data_transfer': return 'AWS Data Transfer Out (DTO)';
      case 'glacier_deep_archive': return 'S3 Glacier Deep Archive';
      case 'cloudfront_data_transfer': return 'CloudFront Data Transfer Out';
      case 'quicksight_pro_spice': return 'QuickSight Pro/SPICE/Admin';
      default: return 'Other Excluded';
    }
  };

  const renderNonEligibleServices = () => {
    const services = calculatorDetails?.services || [];

    // Non-eligible = services with always-excluded spend (Data Transfer,
    // Glacier Deep Archive, AWS Support) per the AWS Migrations Included
    // Services List. Group by service code + exclusion type so the same
    // service split across environments collapses to one row.
    const grouped = {};
    services.forEach(service => {
      const excludedMonthly = parseFloat(service.monthly_always_excluded || 0);
      if (excludedMonthly <= 0) return;

      const breakdown = parseExclusionBreakdown(service.exclusion_breakdown);
      const type = breakdown?.type || 'other';
      // Prefer the breakdown's own ARR (handles upfront-inclusive cases like
      // Glacier); fall back to monthly × 12. Loose != null catches both a
      // missing key (undefined) and a None-serialized ARR (null) — a strict
      // !== undefined check would let null through and render $0.
      const annual = (breakdown?.arr != null) ? breakdown.arr : excludedMonthly * 12;
      const key = `${(service.service_code || '').toLowerCase()}::${type}`;

      if (!grouped[key]) {
        grouped[key] = {
          service_code: service.service_code || '',
          service_name: service.service_name || '',
          exclusion_type: type,
          exclusion_label: exclusionTypeLabel(type),
          reason: withholdVerdict ? '' : breakdown?.reason || '',
          excluded_annual: 0,
          count: 0
        };
      }
      grouped[key].excluded_annual += annual;
      grouped[key].count += 1;
    });

    const nonEligibleServices = Object.values(grouped)
      .sort((a, b) => b.excluded_annual - a.excluded_annual);
    const totalExcluded = nonEligibleServices.reduce((sum, s) => sum + s.excluded_annual, 0);

    return (
      <SpaceBetween size="m">
        <Alert type="info" statusIconAriaLabel="Info" header="What gets excluded">
          Per the AWS Migrations Included Services List, all data transfer costs,
          S3 Glacier Deep Archive, and AWS Support are excluded from Qualification
          ARR. The amounts below are removed before computing MAP funding.
        </Alert>
        <Container>
          <Table
            columnDefinitions={[
              {
                id: 'service_code',
                header: 'Service Code',
                cell: item => item.service_code,
                sortingField: 'service_code'
              },
              {
                id: 'service_name',
                header: 'Service Name',
                cell: item => item.service_name,
                sortingField: 'service_name'
              },
              {
                id: 'exclusion_label',
                header: 'Exclusion Category',
                cell: item => <Badge color="red">{item.exclusion_label}</Badge>,
                sortingField: 'exclusion_label'
              },
              {
                id: 'reason',
                header: 'Reason',
                cell: item => item.reason || '—'
              },
              {
                id: 'excluded_annual',
                header: 'Excluded Annual ARR',
                cell: item => (
                  <Box color="text-status-error" fontWeight="bold">
                    {formatCurrency(item.excluded_annual)}
                  </Box>
                ),
                sortingField: 'excluded_annual'
              },
              {
                id: 'count',
                header: 'Line Items',
                cell: item => item.count,
                sortingField: 'count'
              }
            ]}
            items={nonEligibleServices}
            loadingText="Loading non-eligible services"
            sortingDisabled={false}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No non-eligible services found</b>
                <Box variant="p" color="inherit">
                  This calculator has no excluded spend (Data Transfer, Glacier
                  Deep Archive, or AWS Support).
                </Box>
              </Box>
            }
            header={
              <Header
                counter={`(${nonEligibleServices.length})`}
                description={`Services excluded from Qualification ARR, aggregated by service code and exclusion category — Total Excluded: ${formatCurrency(totalExcluded)}`}
              >
                MAP Non-Eligible Services
              </Header>
            }
            pagination
          />
        </Container>
      </SpaceBetween>
    );
  };

  // Recommendations System
  const generateRecommendations = () => {
    const services = calculatorDetails?.services || [];
    const recommendations = [];
    let id = 1;

    services.forEach(s => {
      const savings = getTotalServiceSavings(s);

      if (savings > 0) {
        const planType = getServicePlanType(s) !== '-' ? getServicePlanType(s) :
          'Standard Reserved Instances (1yr/No Upfront)';
        recommendations.push({
          id: `OPT-${id++}`,
          severity: savings > 10000 ? 'High' : savings > 1000 ? 'Medium' : 'Low',
          service: s.service_name || s.service_code,
          // Mirrors RubiconPricingCalculatorTab: the row only appears for an
          // On-Demand submitted term, so stating it in the title is accurate and
          // makes the issue (not just the fix) visible for rejection notes.
          title: `Switch from On-Demand to ${planType}`,
          description: `${s.service_code} in ${s.region || 'us-east-1'} is using On-Demand pricing. Switching to ${planType} would save ${formatCurrency(savings)}/year.`,
          potentialSavings: savings,
          tags: ['pricing_strategy']
        });
      }

      if (s.prev_generation === 'true' && s.recommended_instance_type) {
        recommendations.push({
          id: `OPT-${id++}`,
          severity: 'Medium',
          service: s.service_name || s.service_code,
          title: `Upgrade to ${s.recommended_instance_type}`,
          description: `Previous-generation instance detected. Upgrade to ${s.recommended_instance_type} for better performance and pricing.`,
          potentialSavings: 0,
          tags: ['instance_upgrade']
        });
      }

      if (s.storage_optimization) {
        recommendations.push({
          id: `OPT-${id++}`,
          severity: 'Low',
          service: s.service_name || s.service_code,
          title: 'Upgrade EBS storage type',
          description: `Storage upgrade recommended: ${s.storage_optimization}`,
          potentialSavings: 0,
          tags: ['storage_upgrade']
        });
      }

      if (s.ola_recommended === 'true') {
        recommendations.push({
          id: `OPT-${id++}`,
          severity: 'Medium',
          service: s.service_name || s.service_code,
          title: 'Optimization and License Assessment (OLA)',
          description: 'Commercial database detected (Oracle/SQL Server). Recommend an OLA engagement to evaluate licensing optimization opportunities.',
          potentialSavings: 0,
          tags: ['ola']
        });
      }
    });

    return recommendations;
  };

  const renderRecommendationsTab = () => {
    const recommendations = generateRecommendations();

    if (recommendations.length === 0) {
      return (
        <Container>
          <Box textAlign="center" color="inherit" padding="xxl">
            <StatusIndicator type="success">
              <Box variant="h3">No Optimization Recommendations</Box>
              <Box variant="p" color="inherit">
                Your current configuration appears to be well optimized. All services meet the optimization thresholds.
              </Box>
            </StatusIndicator>
          </Box>
        </Container>
      );
    }

    return (
      <Container>
        <Table
          columnDefinitions={[
            {
              id: 'id',
              header: 'ID',
              cell: item => item.id,
              sortingField: 'id'
            },
            {
              id: 'severity',
              header: 'Severity',
              cell: item => (
                <Badge color={
                  item.severity === 'High' ? 'red' : 
                  item.severity === 'Medium' ? 'yellow' : 'blue'
                }>
                  {item.severity}
                </Badge>
              ),
              sortingField: 'priority'
            },
            {
              id: 'service',
              header: 'Service',
              cell: item => item.service,
              sortingField: 'service'
            },
            {
              id: 'recommendation',
              header: 'Recommendation',
              cell: item => (
                <Box>
                  <Box variant="strong">{item.title}</Box>
                  {item.description && (
                    <Popover
                      dismissButton={false}
                      position="top"
                      size="medium"
                      triggerType="custom"
                      content={<Box variant="small">{item.description}</Box>}
                    >
                      <Box variant="small" color="text-status-info" display="inline">
                        <Icon name="status-info" size="small" /> Details
                      </Box>
                    </Popover>
                  )}
                </Box>
              )
            },
            {
              id: 'savings',
              header: 'Potential Annual Savings',
              cell: item => (
                <Box color="text-status-success" fontWeight="bold">
                  {formatCurrency(item.potentialSavings)}
                </Box>
              ),
              sortingField: 'potentialSavings'
            },
            {
              id: 'tags',
              header: 'Tags',
              cell: item => (
                <SpaceBetween direction="horizontal" size="xs">
                  {item.tags.map((tag, index) => (
                    <Badge key={index} color="blue">{tag}</Badge>
                  ))}
                </SpaceBetween>
              )
            }
          ]}
          items={recommendations}
          loadingText="Loading recommendations"
          sortingDisabled={false}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No recommendations found</b>
            </Box>
          }
          header={
            <Header
              counter={`(${recommendations.length})`}
              description="Optimization recommendations to reduce costs and improve performance"
            >
              Optimization Recommendations
            </Header>
          }
          footer={
            recommendations.length > 0 && (
              <Box textAlign="center" padding="s">
                <Box variant="strong">
                  Total Potential Annual Savings: {formatCurrency(recommendations.reduce((sum, rec) => sum + rec.potentialSavings, 0))}
                </Box>
              </Box>
            )
          }
        />
      </Container>
    );
  };

  // The Total MAP Funding figures: renderFundElig passes the legacy totals,
  // renderEffectiveTotals the reviewer-adjusted and the agent's.
  const renderTotalsGrid = (totals) => (
    <ColumnLayout columns={3} variant="text-grid">
      <div>
        <Box variant="awsui-key-label">Total Partner Cash</Box>
        <Box fontSize="heading-m" fontWeight="bold">
          {formatCurrency(totals.cash)}
        </Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Total AWS Credits</Box>
        <Box fontSize="heading-m" fontWeight="bold">
          {formatCurrency(totals.credit)}
        </Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Total MAP Funding</Box>
        <Box fontSize="heading-l" fontWeight="bold" color="text-status-success">
          {formatCurrency(totals.all)}
        </Box>
      </div>
    </ColumnLayout>
  );

  // Under effectiveAmounts the reviewer toggles stay: the legacy Step 2's
  // Greenfield, SAP & Oracle and DB & Analytics toggles applied to the agent's
  // rows, summed by component as the agent sums them, PoC in neither. The
  // agent's totals are shown beside them, unchanged, as its maximum.
  const renderEffectiveTotals = () => {
    const toggledOff = {
      'partnerCash.greenfield': !greenfieldEnabled,
      'awsCredits.sapAndOracle': !sapOracleEnabled,
      'awsCredits.dbAndAnalytics': !dbAnalyticsEnabled,
    };
    const adjusted = { cash: 0, credit: 0 };
    effectiveAmounts.rows.forEach((row) => {
      if (row.key !== 'poc' && !toggledOff[row.key]) adjusted[row.component] += Number(row.amount);
    });
    return (
      <SpaceBetween size="m">
        <Box variant="h4">Reviewer-adjusted total (toggles applied, PoC excluded)</Box>
        <SpaceBetween direction="horizontal" size="m">
          <Toggle checked={greenfieldEnabled} onChange={({ detail }) => setGreenfieldEnabled(detail.checked)}>
            Include Greenfield SPI
          </Toggle>
          <Toggle checked={sapOracleEnabled} onChange={({ detail }) => setSapOracleEnabled(detail.checked)}>
            Include SAP &amp; Oracle
          </Toggle>
          <Toggle checked={dbAnalyticsEnabled} onChange={({ detail }) => setDbAnalyticsEnabled(detail.checked)}>
            Include DB &amp; Analytics
          </Toggle>
        </SpaceBetween>
        {renderTotalsGrid({ ...adjusted, all: adjusted.cash + adjusted.credit })}
        <Box variant="h4">Agent-computed maximum (all incentives, before toggles, PoC excluded)</Box>
        {renderTotalsGrid(effectiveAmounts.totals)}
      </SpaceBetween>
    );
  };

  // The 3P Bedrock policy UI: the agent trust notice, the panel and the
  // agent's Step 2. On the FR page none of it reads the live recompute, so
  // when that failed or returned no fundingEligibility the fallbacks render
  // it too, withTotals adding Total MAP Funding.
  const renderPersistedBedrock3p = (withTotals = false) => (
    <>
      {bedrock3pAgent?.trusted === false && (
        <Alert type="warning">
          {`The MAP agent did not use this policy block: ${bedrock3pAgent.trust_reason}`}
        </Alert>
      )}

      {bedrock3pBlock && (
        <Bedrock3pPolicyPanel block={bedrock3pBlock} formatCurrency={formatCurrency} analyzer={!!preloadedData} />
      )}

      {/* Funding Amounts from the agent's effectiveAmounts (3P Bedrock cap
          enforce): shown whenever the agent persisted them, whatever tier
          the live recompute reports. */}
      {effectiveAmounts && (
        <Container header={<Header variant="h3">Step 2 — Funding Amounts</Header>}>
          <Bedrock3pEffectiveAmounts effectiveAmounts={effectiveAmounts} formatCurrency={formatCurrency} />
        </Container>
      )}

      {withTotals && effectiveAmounts && (
        <Container header={<Header variant="h3">Total MAP Funding</Header>}>
          {renderEffectiveTotals()}
        </Container>
      )}
    </>
  );

  const renderFundElig = () => {
    const fundingData = calculatorDetails?.fundingEligibility;
    if (!fundingData) {
      // Fallback: show basic MAP eligibility info when backend hasn't
      // computed funding eligibility yet (graceful degradation).
      const calcValidation = calculatorDetails?.validation || {};
      const calcOptimizationMet = calcValidation.optimization_metrics
        ? calcValidation.optimization_metrics.not_optimized_percentage <= 10.0
        : optimizationThresholdMet;

      return (
        <SpaceBetween size="l">
          <Container>
            <Alert type="info" header="Funding Eligibility Information">
              <SpaceBetween size="s">
                <Box>
                  <Box variant="strong">MAP Qualified ARR:</Box> {formatCurrency(qualifiedARR)}
                </Box>
                {!withholdVerdict && (
                  <Box>
                    <Box variant="strong">Optimization Status:</Box> {calcOptimizationMet ? 'Meets threshold' : 'Below threshold'}
                  </Box>
                )}
              </SpaceBetween>
            </Alert>
          </Container>
          {renderPersistedBedrock3p(true)}
        </SpaceBetween>
      );
    }

    const fc = (amount) => formatCurrency(amount);
    const tier = fundingData.programTier;
    const isQualified = tier === 'MAP_LITE' || tier === 'MAP_LARGE';

    // TODO: These thresholds are hardcoded and must match backend logic.
    // Ideally the backend should return them in the fundingEligibility response.
    const minThreshold = 100000;  // $100K / €100K for both standard and ESC
    const maxThreshold = fundingData.isESC ? 2000000 : 10000000;

    // Recalculate totals based on toggles. The blind page shows no Step 2 or
    // totals, and its body has no awsCredits or partnerCash, so it reads neither.
    let totalCredits = 0;
    let totalPartnerCash = 0;
    if (!withholdVerdict) {
      const sapOracleAmount = sapOracleEnabled ? fundingData.awsCredits.sapAndOracle.amount : 0;
      const dbAnalyticsAmount = dbAnalyticsEnabled ? fundingData.awsCredits.dbAndAnalytics.amount : 0;
      const greenfieldAmount = greenfieldEnabled ? fundingData.partnerCash.greenfield.amount : 0;
      totalCredits = fundingData.awsCredits.migrateAndModernize.amount + sapOracleAmount + dbAnalyticsAmount;
      totalPartnerCash = fundingData.partnerCash.assess.amount + fundingData.partnerCash.mobilize.amount + greenfieldAmount + fundingData.partnerCash.modernization.amount;
    }
    const totalFunding = totalPartnerCash + totalCredits;

    const getTierBadge = () => {
      if (tier === 'MAP_LARGE') return <Badge color="blue">MAP LARGE</Badge>;
      if (tier === 'MAP_LITE') return <Badge color="green">MAP LITE</Badge>;
      if (tier === 'BELOW_THRESHOLD') return <Badge color="red">Below Threshold</Badge>;
      if (tier === 'ABOVE_MAXIMUM') return <Badge color="red">Above Maximum</Badge>;
      return <Badge color="grey">{tier}</Badge>;
    };

    return (
      <SpaceBetween size="l">
        {/* Qualification ARR Summary */}
        <Container header={
          <Header variant="h3">
            Step 1 — Qualification ARR
          </Header>
        }>
          <ColumnLayout columns={2} variant="text-grid">
            <SpaceBetween size="s">
              <div>
                <Box variant="awsui-key-label">Annual Run Rate (before exclusions)</Box>
                <Box>{fc(fundingData.annualRunRate)}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Deductions</Box>
                <SpaceBetween size="xxs">
                  {fundingData.deductions.dataTransfer > 0 && (
                    <Box variant="small">Data Transfer: -{fc(fundingData.deductions.dataTransfer)}</Box>
                  )}
                  {fundingData.deductions.glacierDeepArchive > 0 && (
                    <Box variant="small">S3 Glacier Deep Archive: -{fc(fundingData.deductions.glacierDeepArchive)}</Box>
                  )}
                  {fundingData.deductions.awsSupport > 0 && (
                    <Box variant="small">AWS Support: -{fc(fundingData.deductions.awsSupport)}</Box>
                  )}
                  {fundingData.deductions.otherExcluded > 0 && (
                    <Box variant="small">Other Excluded: -{fc(fundingData.deductions.otherExcluded)}</Box>
                  )}
                  {fundingData.deductions.threeYearAmortization > 0 && (
                    <Popover
                      dismissButton={false}
                      position="right"
                      size="medium"
                      triggerType="custom"
                      content={<Box>Services with 3-year upfront payments have their upfront cost divided by 3 to reflect the annual steady-state ARR used for funding calculations.</Box>}
                    >
                      <Box variant="small" color="text-status-warning">3-Year Amortization: -{fc(fundingData.deductions.threeYearAmortization)}</Box>
                    </Popover>
                  )}
                  {fundingData.deductions.savingsPlansDiscounts > 0 && (
                    <Box variant="small">SP/RI Optimization Savings: -{fc(fundingData.deductions.savingsPlansDiscounts)}</Box>
                  )}
                  {!(fundingData.deductions.dataTransfer > 0 || fundingData.deductions.glacierDeepArchive > 0 || fundingData.deductions.awsSupport > 0 || fundingData.deductions.otherExcluded > 0 || fundingData.deductions.threeYearAmortization > 0 || fundingData.deductions.savingsPlansDiscounts > 0) && (
                    <Box variant="small" color="text-status-inactive">{fc(0)}</Box>
                  )}
                </SpaceBetween>
              </div>
            </SpaceBetween>
            <SpaceBetween size="s">
              <div>
                <Box variant="awsui-key-label">Final MAP Qualified ARR</Box>
                <Box fontSize="heading-l" fontWeight="bold" color="text-status-success">
                  {fc(fundingData.qualificationARR)}
                </Box>
              </div>
              {!withholdVerdict && (
                <div>
                  <Box variant="awsui-key-label">Program Tier</Box>
                  {getTierBadge()}
                </div>
              )}
              {fundingData.isESC && (
                <div>
                  <Box variant="awsui-key-label">Region</Box>
                  <Badge color="blue">European Sovereign Cloud (ESC)</Badge>
                </div>
              )}
            </SpaceBetween>
          </ColumnLayout>
        </Container>

        {renderPersistedBedrock3p()}

        {/* Funding Amounts */}
        {!withholdVerdict && isQualified && !effectiveAmounts && (
          <Container header={<Header variant="h3">Step 2 — Funding Amounts</Header>}>
            <SpaceBetween size="m">
              {/* Partner Cash */}
              <Box variant="h4">
                Partner Cash
                <Box variant="small" display="inline" margin={{ left: 's' }}>
                  <Toggle
                    checked={greenfieldEnabled}
                    onChange={({ detail }) => setGreenfieldEnabled(detail.checked)}
                  >
                    Include Greenfield SPI
                  </Toggle>
                </Box>
              </Box>
              <Table
                variant="embedded"
                columnDefinitions={[
                  { id: 'type', header: 'Funding Type', cell: item => item.type },
                  { id: 'percentage', header: 'Rate', cell: item => item.percentage },
                  { id: 'calculated', header: 'Calculated', cell: item => fc(item.calculated) },
                  { id: 'cap', header: 'Cap', cell: item => fc(item.cap) },
                  { id: 'amount', header: 'Amount', cell: item => <Box fontWeight="bold">{fc(item.amount)}</Box> },
                  { id: 'notes', header: 'Notes', cell: item => item.notes || '-' },
                ]}
                items={[
                  {
                    type: 'Assess',
                    percentage: '5%',
                    calculated: fundingData.partnerCash.assess.calculated,
                    cap: fundingData.partnerCash.assess.cap,
                    amount: fundingData.partnerCash.assess.amount,
                  },
                  {
                    type: 'Mobilize',
                    percentage: '20%',
                    calculated: fundingData.partnerCash.mobilize.calculated,
                    cap: fundingData.partnerCash.mobilize.cap,
                    amount: fundingData.partnerCash.mobilize.amount,
                  },
                  {
                    type: 'Greenfield SPI',
                    percentage: '10%',
                    calculated: fundingData.partnerCash.greenfield.calculated,
                    cap: fundingData.partnerCash.greenfield.cap,
                    amount: greenfieldEnabled ? fundingData.partnerCash.greenfield.amount : 0,
                    notes: !greenfieldEnabled ? 'Toggle to include' : '',
                  },
                  {
                    type: 'Modernization Pathways SPI',
                    percentage: '10%',
                    calculated: fundingData.partnerCash.modernization.calculated,
                    cap: fundingData.partnerCash.modernization.cap,
                    amount: fundingData.partnerCash.modernization.amount,
                    notes: fundingData.partnerCash.modernization.eligible
                      ? `Index: ${fundingData.partnerCash.modernization.modernizationIndex}%`
                      : fundingData.partnerCash.modernization.reason,
                  },
                ]}
              />
              <Box textAlign="right">
                <Box variant="strong">Total Partner Cash: {fc(totalPartnerCash)}</Box>
              </Box>

              {/* AWS Credits */}
              <Box variant="h4" margin={{ top: 'm' }}>
                AWS Credits
                <Box variant="small" display="inline" margin={{ left: 's' }}>
                  <Toggle
                    checked={sapOracleEnabled}
                    onChange={({ detail }) => setSapOracleEnabled(detail.checked)}
                  >
                    Include SAP &amp; Oracle
                  </Toggle>
                </Box>
                <Box variant="small" display="inline" margin={{ left: 's' }}>
                  <Toggle
                    checked={dbAnalyticsEnabled}
                    onChange={({ detail }) => setDbAnalyticsEnabled(detail.checked)}
                  >
                    Include DB &amp; Analytics
                  </Toggle>
                </Box>
              </Box>
              <Table
                variant="embedded"
                columnDefinitions={[
                  { id: 'type', header: 'Credit Type', cell: item => item.type },
                  { id: 'percentage', header: 'Rate', cell: item => item.percentage },
                  { id: 'eligibleARR', header: 'Eligible ARR', cell: item => fc(item.eligibleARR) },
                  { id: 'amount', header: 'Amount', cell: item => <Box fontWeight="bold">{fc(item.amount)}</Box> },
                  { id: 'notes', header: 'Notes', cell: item => item.notes || '-' },
                ]}
                items={[
                  {
                    type: 'Migrate & Modernize',
                    percentage: `${fundingData.awsCredits.migrateAndModernize.percentage}%`,
                    eligibleARR: fundingData.awsCredits.migrateAndModernize.eligibleARR,
                    amount: fundingData.awsCredits.migrateAndModernize.amount,
                    notes: tier === 'MAP_LARGE' ? 'Large (25%)' : 'Lite (15%)',
                  },
                  {
                    type: 'SAP & Oracle',
                    percentage: '50%',
                    eligibleARR: fundingData.awsCredits.sapAndOracle.eligibleARR,
                    amount: sapOracleEnabled ? fundingData.awsCredits.sapAndOracle.amount : 0,
                    notes: !sapOracleEnabled
                      ? 'Toggle to include'
                      : fundingData.awsCredits.sapAndOracle.eligibleARR <= 0
                        ? 'No eligible ARR'
                        : fundingData.awsCredits.sapAndOracle.creditEligible
                          ? 'Enhanced credit applied'
                          : `SAP/Oracle scope below ${fc(fundingData.awsCredits.sapAndOracle.threshold)} — standard M&M credit only`,
                  },
                  {
                    type: 'DB & Analytics',
                    percentage: '10%',
                    eligibleARR: fundingData.awsCredits.dbAndAnalytics.eligibleARR,
                    amount: dbAnalyticsEnabled ? fundingData.awsCredits.dbAndAnalytics.amount : 0,
                    notes: !dbAnalyticsEnabled ? 'Toggle to include' : '',
                  },
                ]}
              />
              <Box textAlign="right">
                <Box variant="strong">Total AWS Credits: {fc(totalCredits)}</Box>
              </Box>

              {/* PoC */}
              <Box variant="h4" margin={{ top: 'm' }}>PoC</Box>
              <ColumnLayout columns={4} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Rate</Box>
                  <Box>10% of Qualification ARR</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Calculated</Box>
                  <Box>{fc(fundingData.poc.calculated)}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Cap</Box>
                  <Box>{fc(fundingData.poc.cap)}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Amount</Box>
                  <Box fontWeight="bold">{fc(fundingData.poc.amount)}</Box>
                </div>
              </ColumnLayout>
            </SpaceBetween>
          </Container>
        )}

        {/* Totals */}
        {!withholdVerdict && (isQualified || effectiveAmounts) && (
          <Container header={<Header variant="h3">Total MAP Funding</Header>}>
            {effectiveAmounts
              ? renderEffectiveTotals()
              : renderTotalsGrid({ cash: totalPartnerCash, credit: totalCredits, all: totalFunding })}
            {fundingData.isESC && fundingData.escPaymentModel && (
              <Alert type="info" header="ESC Payment Model" statusIconAriaLabel="Info">
                <SpaceBetween size="xxs">
                  <Box>Payment Structure: Two-tranche payment (25%/75%)</Box>
                  <Box>First tranche (25%): Disbursed at {fc(fundingData.escPaymentModel.firstTrancheThreshold)} customer spend</Box>
                  <Box>Second tranche (75%): Disbursed at migration completion (customer attestation)</Box>
                  <Box>Customer Sign-off (CST) required. No tagging required for ESC migrations.</Box>
                </SpaceBetween>
              </Alert>
            )}
          </Container>
        )}

        {/* Not Qualified Message. Beside the agent's effectiveAmounts it is
            only the live recompute's reading, not the record they came from. */}
        {!withholdVerdict && !isQualified && (
          <Alert
            type={effectiveAmounts ? 'info' : 'warning'}
            header={effectiveAmounts ? 'Live recompute: not qualified for MAP Funding' : 'Not Qualified for MAP Funding'}
          >
            {tier === 'BELOW_THRESHOLD'
              ? `Qualification ARR of ${fc(fundingData.qualificationARR)} is below the minimum threshold of ${fc(minThreshold)}. MAP requires a minimum ARR of ${fc(minThreshold)}.`
              : tier === 'ABOVE_MAXIMUM'
                ? `Qualification ARR of ${fc(fundingData.qualificationARR)} exceeds the maximum threshold of ${fc(maxThreshold)}.`
                : `Qualification ARR of ${fc(fundingData.qualificationARR)} does not meet MAP program requirements.`
            }
            {effectiveAmounts && ' Step 2 and Total MAP Funding show the amounts the MAP agent recorded at validation, not this recompute.'}
          </Alert>
        )}

        {/* Tier Downgrade Warning — exclusions caused a tier change */}
        {!withholdVerdict && fundingData.tierDowngrade && (
          <Alert type="info" header="Tier Impact from Exclusions">
            {fundingData.tierDowngrade.message}
          </Alert>
        )}

        {/* Fargate + EKS Advisory — informational cash-only note (P474687015) */}
        {!withholdVerdict && fundingData.fargateAdvisory && (
          <Alert type="info" header="Fargate Credit Eligibility">
            {fundingData.fargateAdvisory.message}
          </Alert>
        )}
      </SpaceBetween>
    );
  };

  // Modernization Tab Content
  const renderModernizationTab = () => {
    const modernizationData = calculatorDetails?.modernizationPathways || {};
    return (
      <ModernizationCharts
        modernizationData={modernizationData}
        formatCurrency={formatCurrency}
      />
    );
  };

  const renderMainTabContent = () => {
    const subTabs = [
      {
        id: 'annual-run-rate',
        label: 'Annual Run Rate',
        content: renderAnnualRunRate()
      },
      // Withheld from a blind reviewer: it splits the services by eligibility tier.
      ...(withholdVerdict ? [] : [{
        id: 'eligible-services',
        label: 'Eligible Services',
        content: renderEligibleServices()
      }]),
      {
        id: 'non-eligible-services',
        label: 'Non-Eligible Services',
        content: renderNonEligibleServices()
      },
      {
        id: 'fund-elig',
        label: 'Funding Eligibility',
        content: renderFundElig()
      }
    ];

    return (
      <Tabs
        tabs={subTabs}
        activeTabId={activeSubTab}
        onChange={({ detail }) => setActiveSubTab(detail.activeTabId)}
      />
    );
  };

  // Calculate if we should show optional tabs
  const modernizationData = calculatorDetails?.modernizationPathways || {};
  const modernServices = modernizationData.pathways
    ?.filter(p => p.name !== 'Non Modern')
    .flatMap(p => p.services) || [];
  const recommendations = generateRecommendations();

  const mainTabs = [
    {
      id: 'general',
      label: 'General',
      content: renderEnhancedServicesTable()
    },
    ...(modernServices.length > 0 || services.length > 0 ? [{
      id: 'modernization',
      label: 'Modernization',
      content: renderModernizationTab()
    }] : []),
    ...(recommendations.length > 0 ? [{
      id: 'recommendations',
      label: 'Recommendations',
      content: renderRecommendationsTab()
    }] : []),
    {
      id: 'migration-acceleration',
      label: 'Migration Acceleration Program',
      content: renderMainTabContent()
    }
  ];

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          Loading pricing calculator details...
        </Box>
      </Container>
    );
  }

  // Check if pricing calculator data is not available
  if (error || !calculatorDetails || !calculatorDetails.services || calculatorDetails.services.length === 0) {
    // The Analyzer has no persisted record; on the FR page the agent's record
    // still renders when the live recompute is unavailable.
    const showPersisted = !preloadedData && (bedrock3pBlock || effectiveAmounts);
    return (
      <SpaceBetween size="l">
        <Container>
          <Box textAlign="center" padding="xxl">
            <StatusIndicator type="info">
              <Box variant="h3">Pricing Calculator Not Available</Box>
              <Box variant="p" color="inherit" padding={{ top: 's' }}>
                {showPersisted
                  ? 'Live pricing calculator details are not available for this request. The 3P Bedrock policy below is what the MAP agent recorded at validation.'
                  : <>
                      This funding request does not have pricing calculator data available.
                      The pricing calculator may not have been completed or submitted for this request.
                    </>}
              </Box>
            </StatusIndicator>
          </Box>
        </Container>
        {showPersisted && renderPersistedBedrock3p(true)}
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="l">
      {/* EKS / ECS / Move-to-Containers disclaimer */}
      {services.some(s => /eks|ecs/i.test(s.service_code || '')) && (
        <Alert type="warning" header="Container Workloads - Pricing Estimation Note" statusIconAriaLabel="Warning">
          The AWS Pricing Calculator includes Elastic Kubernetes Service (EKS) and Elastic Compute Cloud (EC2) estimations. Chronos cannot automatically associate EC2 instances with their corresponding EKS clusters. This may result in an inaccurate modernization index, as EC2 costs are not included in the modern services ARR.
        </Alert>
      )}
      {/* Pricing Calculator Link */}
      {(() => {
        const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
        const rawCalculatorUrl = validatedCalculatorUrl || fundRequest.artifacts?.pricingCalculatorLinks?.[0] ||
                             pricingData.calculator_url;
        // PenPal #11 (CWE-601): pricingCalculatorLinks[] is partner-supplied and
        // reaches window.open unvalidated — sanitize before binding to the sink.
        const calculatorUrl = sanitizeCalculatorUrl(rawCalculatorUrl);

        if (calculatorUrl) {
          return (
            <Container>
              <Box textAlign="center" padding="m">
                <Button
                  variant="primary"
                  iconName="external"
                  onClick={() => window.open(calculatorUrl, '_blank')}
                >
                  Open AWS Pricing Calculator
                </Button>
              </Box>
            </Container>
          );
        }
        return null;
      })()}

      {/* Enhanced Dashboard Summary - Always visible */}
      {renderEnhancedSummary()}

      {/* Modernization Index */}
      {(() => {
        const modernizationData = calculatorDetails?.modernizationPathways || {};
        const modernizationIndex = modernizationData.modernizationIndex || 0;
        const totalModernArr = modernizationData.modernARR || 0;
        const totalAnnualCost = modernizationData.totalARR || 0;
        
        // Use backend pathway data
        const pathwayGroups = {};
        if (modernizationData.pathways) {
          modernizationData.pathways.forEach(pathway => {
            pathwayGroups[pathway.name] = {
              count: pathway.serviceCount,
              arr: pathway.arr
            };
          });
        }

        return (
          <Container header={<Header variant="h2">Modernization Index</Header>}>
            {loading ? (
              <Box textAlign="center" padding="l">
                <Spinner size="normal" />
                <Box variant="p" color="text-body-secondary">Loading modernization analysis...</Box>
              </Box>
            ) : calculatorDetails && services.length > 0 ? (
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Overall Modernization Score</Box>
                  <Box variant="h2" color={modernizationIndex > 50 ? "text-status-success" : modernizationIndex > 25 ? "text-status-warning" : "text-status-error"}>
                    {modernizationIndex}%
                  </Box>
                  <ProgressBar 
                    value={modernizationIndex} 
                    variant={modernizationIndex > 50 ? "success" : "warning"}
                    additionalInfo={`${formatCurrency(totalModernArr)} of ${formatCurrency(totalAnnualCost)} ARR is modern`}
                  />
                </div>
                <div>
                  <Box variant="awsui-key-label">Total Modern ARR</Box>
                  <Box variant="h3" color="text-status-success">
                    {formatCurrency(totalModernArr)}
                  </Box>
                  <Box variant="small">ARR from modern AWS services</Box>
                </div>
              </ColumnLayout>
            ) : (
              <Alert type="info" header="Modernization Analysis Not Available">
                Modernization pathways analysis is not available for this fund request. This may indicate that the pricing calculator has not been processed yet or no calculator URL was provided.
              </Alert>
            )}
          </Container>
        );
      })()}

      {/* Main Dashboard Tabs */}
      <Tabs
        tabs={mainTabs}
        activeTabId={activeMainTab}
        onChange={({ detail }) => setActiveMainTab(detail.activeTabId)}
        variant="container"
      />
    </SpaceBetween>
  );
};

export default PricingCalculatorTab;